<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  utilisateur: { type: Object, default: () => ({}) },
  roles:       { type: Array,  default: () => [] },
  errors:      { type: Object, default: () => ({}) },
})

const form = useForm({
  email:                 props.utilisateur.email                 || '',
  nom:                   props.utilisateur.nom                   || '',
  prenom:                props.utilisateur.prenom                || '',
  password:              '',
  password_confirmation: '',
  role_id:               props.utilisateur.role_id               || '',
  actif:                 props.utilisateur.actif ?? true,
})

const { error: toastError } = useToast()

const fieldLabels = {
  email:                 'Courriel',
  nom:                   'Nom',
  prenom:                'Prénom',
  password:              'Mot de passe',
  password_confirmation: 'Confirmation du mot de passe',
  role_id:               'Rôle',
  role:                  'Rôle',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}

function submit() {
  form.transform(data => ({ utilisateur: data })).post('/utilisateurs', {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

function cancel() {
  window.location.href = '/utilisateurs'
}

const fieldError = (field) => {
  const aliases = { role_id: 'role' }
  const alt = aliases[field]
  return form.errors[field]
    || props.errors[field]?.[0]
    || (alt && (form.errors[alt] || props.errors[alt]?.[0]))
    || null
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Utilisateurs &rsaquo; Nouvel utilisateur
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Nouvel Utilisateur
            </h1>
          </div>
          <a
            href="/utilisateurs"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Form Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Email -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Courriel <span class="text-error">*</span>
              </label>
              <input
                v-model="form.email"
                type="email"
                placeholder="utilisateur@example.com"
                class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                :class="fieldError('email') ? 'ring-2 ring-error/40' : ''"
              />
              <p v-if="fieldError('email')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('email') }}</p>
            </div>

            <!-- Nom + Prénom -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Nom
                </label>
                <input
                  v-model="form.nom"
                  type="text"
                  placeholder="Nom de famille"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('nom') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('nom')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('nom') }}</p>
              </div>
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Prénom
                </label>
                <input
                  v-model="form.prenom"
                  type="text"
                  placeholder="Prénom"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                />
              </div>
            </div>

            <!-- Mot de passe -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Mot de passe <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.password"
                  type="password"
                  placeholder="••••••••"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('password') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('password')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('password') }}</p>
              </div>
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Confirmation <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.password_confirmation"
                  type="password"
                  placeholder="••••••••"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('password_confirmation') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('password_confirmation')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('password_confirmation') }}</p>
              </div>
            </div>

            <!-- Rôle + Actif -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Rôle <span class="text-error">*</span>
                </label>
                <div class="relative">
                  <select
                    v-model="form.role_id"
                    class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 appearance-none transition-all outline-none"
                    :class="fieldError('role_id') ? 'ring-2 ring-error/40' : ''"
                  >
                    <option value="">— Sélectionner —</option>
                    <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.nom }}</option>
                  </select>
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-outline material-symbols-outlined text-base">expand_more</span>
                </div>
                <p v-if="fieldError('role_id')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('role_id') }}</p>
              </div>
              <div class="flex flex-col justify-end pb-1">
                <label class="flex items-center gap-3 cursor-pointer group">
                  <div
                    @click="form.actif = !form.actif"
                    :class="form.actif ? 'bg-primary' : 'bg-surface-container-highest'"
                    class="relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer"
                  >
                    <span
                      :class="form.actif ? 'translate-x-5' : 'translate-x-1'"
                      class="absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 block"
                    ></span>
                  </div>
                  <span class="text-[10px] font-black uppercase tracking-widest text-outline group-hover:text-on-surface transition-colors">
                    Compte actif
                  </span>
                </label>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4 pt-6">
              <button
                type="submit"
                :disabled="form.processing"
                class="flex-1 machined-sheen text-on-primary font-black py-4 rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">person_add</span>
                Créer l'utilisateur
              </button>
              <button
                type="button"
                @click="cancel"
                class="px-8 py-4 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </button>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
